import { type RefObject, useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function listFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  )
}

/**
 * Dialog keyboard a11y for the SPA Modal fork: opener restore on true close,
 * Tab cycle + Escape while the layer is active, and no restore when temporarily
 * covered (e.g. `hidden` behind a nested HPO search portal).
 *
 * Layer model:
 * - `isOpen` false → closed (restore opener on transition from open)
 * - `isOpen` && !`isCovered` → trap active
 * - `isOpen` && `isCovered` → trap off, focus stays with the nested UI
 *
 * On uncover, prefer the in-dialog element that had focus before cover rather
 * than always jumping to the first focusable.
 */
export function useDialogFocusTrap({
  isOpen,
  isCovered = false,
  dialogRef,
  onEscape,
}: {
  isOpen: boolean
  isCovered?: boolean
  dialogRef: RefObject<HTMLElement | null>
  onEscape: () => void
}): void {
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const preCoverFocusRef = useRef<HTMLElement | null>(null)
  // Live open flag for trap cleanup: capture pre-cover focus only while the
  // dialog remains open (cover), never on true close. Updated during render so
  // cleanup sees the post-commit value.
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen

  // Opener capture/restore is tied to isOpen only — covering must not restore.
  useEffect(() => {
    if (!isOpen) {
      preCoverFocusRef.current = null
      return
    }
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    return () => {
      const restore = restoreFocusRef.current
      if (restore && document.contains(restore)) {
        restore.focus()
      }
      restoreFocusRef.current = null
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || isCovered) return

    // Read dialogRef inside callbacks — AnimatePresence may attach the node
    // after this effect schedules, so a closed-over snapshot stays null forever.
    const focusOnActivate = () => {
      const dialog = dialogRef.current
      if (!dialog) return

      const preCover = preCoverFocusRef.current
      preCoverFocusRef.current = null
      if (preCover && document.contains(preCover) && dialog.contains(preCover)) {
        preCover.focus()
        return
      }

      const active = document.activeElement
      if (active instanceof HTMLElement && dialog.contains(active)) {
        return
      }

      const items = listFocusable(dialog)
      ;(items[0] ?? dialog).focus()
    }
    const focusTimer = window.setTimeout(focusOnActivate, 0)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onEscapeRef.current()
        return
      }
      if (event.key !== 'Tab') return

      const dialog = dialogRef.current
      if (!dialog) return

      const items = listFocusable(dialog)
      if (items.length === 0) {
        event.preventDefault()
        dialog.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      const activeIsInside = active ? dialog.contains(active) : false

      if (event.shiftKey) {
        if (!activeIsInside || active === first) {
          event.preventDefault()
          last.focus()
        }
      } else if (!activeIsInside || active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown)
      // Trap teardown while still open = cover (or remount). React runs this
      // cleanup before sibling/nested focus effects, so snapshot here — not in a
      // later `isCovered` setup effect, which can lose the race to HPO focus.
      // Live dialogRef read is intentional (AnimatePresence may attach late;
      // a closed-over node from effect start stays null).
      if (!isOpenRef.current) return
      // eslint-disable-next-line react-hooks/exhaustive-deps -- live dialogRef at cleanup
      const dialog = dialogRef.current
      const active = document.activeElement
      if (dialog && active instanceof HTMLElement && dialog.contains(active)) {
        preCoverFocusRef.current = active
      }
    }
  }, [isOpen, isCovered, dialogRef])
}
